import React from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'

import svgHelpers from '../../../helpers/functions/svgHelpers'
import Style from './style'
import { COLOR, IMG } from '../../../themes'
import { FALLBACK_SPACE_TYPE_THEME, SPACE_TYPE } from '../../../helpers/constants/listRoom'
import { hexToRgba } from '../../../helpers'
import { FloorPlanLabel } from '../../../components/FloorPlanLabel'

const BADGE_PX = 28
const LABEL_MIN_PX = 40
const LABEL_SPACING_PX = 8
const LABEL_MAX_PX = 160

const showsOwner = (room) => (
  room.spaceType === SPACE_TYPE.DESK && Number(room.isUsing) === 1 && Boolean(room.ownerName)
)

const getSpaceLabel = (room) => (showsOwner(room) ? room.ownerName : room.roomName)

class LinkOverlayComponent extends React.Component {
  constructor(props) {
    super(props);

    this.areaRef = React.createRef();
    this.textRef = React.createRef();
    this.circleRef = React.createRef();
    this.svgRef = React.createRef();
    this.state = { renderedWidth: 0 };
  }

  componentDidMount () {
    const svg = this.svgRef.current
    if (!svg) return
    const measure = () => this.setState({ renderedWidth: svg.getBoundingClientRect().width })
    measure()
    this.resizeObserver = new ResizeObserver(measure)
    this.resizeObserver.observe(svg)
  }

  componentWillUnmount () {
    if (this.resizeObserver) this.resizeObserver.disconnect()
  }

  getScale () {
    const { imageWidth } = this.props
    return this.state.renderedWidth > 0 && imageWidth > 0 ? this.state.renderedWidth / imageWidth : 1
  }

  renderLink (id, coord, isUsing, name) {
    const className = isUsing ? 'busy pointer' : 'available pointer'
    const colorTheme = this.props.settingTheme?.colorTheme
    const busyVars = isUsing
      ? {
          '--busy-fill': hexToRgba(colorTheme, 0.7),
          '--busy-fill-hover': hexToRgba(colorTheme, 0.85),
          '--busy-stroke': hexToRgba(colorTheme, 0.55),
        }
      : undefined
    return (
      <React.Fragment key={id} >
        <g className={className} key={id} style={busyVars} onClick={(event) => this.props.handleClick(event, id)}>
          <polygon points={coord} vectorEffect='non-scaling-stroke' ref={this.areaRef}/>
        </g>
      </React.Fragment>
    )
  }

  renderIcon (id, nickname, centerPoint, shape, isUsing, spaceType = 'other', isOwnerShown = false) {
    try {
      centerPoint = JSON.parse(centerPoint)
      shape = JSON.parse(shape)
    } catch (_) {
      return null
    }
    if (centerPoint == null || shape == null) return null

    const theme = this.props.themeConfig?.spaceType?.[spaceType] || FALLBACK_SPACE_TYPE_THEME[spaceType] || FALLBACK_SPACE_TYPE_THEME.other;
    const bg = theme.background || theme.bg;
    const fg = theme.foreground || theme.text;
    const iconName = isOwnerShown ? 'ic_lucide_user.svg' : `ic_${spaceType}.svg`;
    
    const colorTheme = this.props.settingTheme?.colorTheme
    const scale = this.getScale();
    const badgeSize = BADGE_PX / scale;
    const iconSize = badgeSize * 0.55;
    const labelMaxWidth = Math.min(Math.max(shape.width * scale - LABEL_SPACING_PX, LABEL_MIN_PX), LABEL_MAX_PX);
    const left = centerPoint.x - shape.width / 2;
    const top = centerPoint.y - shape.height / 2;
    const busyPillStyle = isUsing
      ? { boxShadow: `0 2px 6px ${hexToRgba(colorTheme, 0.28)}` }
      : undefined;

    return (
      <g key={'icon_' + id} className="room-group">
        <foreignObject
          x={left}
          y={top}
          width={shape.width}
          height={shape.height}
          className='pointer room-foreign-object'
          onClick={(event) => this.props.handleClick(event, id)}
          style={{ overflow: 'visible' }}
          ref={this.circleRef}
        >
          <div className='room-box-inner' style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            boxSizing: 'border-box',
          }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: `${badgeSize}px`,
              height: `${badgeSize}px`,
              borderRadius: '50%',
              backgroundColor: bg,
            }}>
              <div style={{
                width: `${iconSize}px`,
                height: `${iconSize}px`,
                backgroundColor: fg,
                WebkitMask: `url(${IMG[iconName]}) no-repeat center`,
                WebkitMaskSize: 'contain',
                mask: `url(${IMG[iconName]}) no-repeat center`,
                maskSize: 'contain'
              }} />
            </div>
          </div>
        </foreignObject>
        <foreignObject
          x={centerPoint.x}
          y={centerPoint.y + badgeSize / 2 + 4 / scale}
          width={1}
          height={1}
          className='room-label'
          style={{ pointerEvents: 'none', overflow: 'visible' }}
        >
          <FloorPlanLabel name={nickname} maxWidth={labelMaxWidth} scale={scale} style={busyPillStyle} />
        </foreignObject>
        <foreignObject
          x={left + shape.width / 2}
          y={top - 16}
          width={1}
          height={1}
          className='static-tooltip'
          style={{ pointerEvents: 'none', overflow: 'visible' }}
        >
           <p className='svg_tooltip_p'>{nickname}</p>
        </foreignObject>
      </g>
    )
  }

  render () {
    const props = Object.assign({}, this.props)
    const width = '100%'
    const height = '100%'
    return (
      <Style
        colorTheme={this.props.settingTheme.colorTheme}
      >
        <svg
          ref={this.svgRef}
          id='linkOverlay'
          className='linkOverlay'
          viewBox={svgHelpers.getViewBox(props.imageWidth, props.imageHeight)}
          xmlns='http://www.w3.org/2000/svg'
          preserveAspectRatio='none'
          style={{ width: (props.width != null) ? props.width : width, height: (props.height != null) ? props.height : height }}
        >
          {props.roomList.map((room) => this.renderLink(room.roomId, room.coord, room.isUsing, room.roomName))}
          {props.roomList.map((room) => this.renderIcon(room.roomId, getSpaceLabel(room), room.centerPoint, room.shape, room.isUsing, room.spaceType, showsOwner(room)))}
        </svg>
      </Style>
    )
  }
}

LinkOverlayComponent.propTypes = {
  roomList: PropTypes.array.isRequired,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number
}

export default LinkOverlayComponent

